import { startsWith } from "ramda";
import type { CreditCardFormValues } from "components/digital_gift_cards/DigitalGiftCardCreditCardForm";
import VALIDATION_MESSAGES from "helpers/validation/messages";
import Validators from "helpers/validation/validators";

export function validateExpirationInput(value: string) {
  const strippedValue = value.replace(/[^0-9]+/g, "");
  const monthPart = strippedValue.slice(0, 2);
  const yearPart = strippedValue.slice(2, 4);

  if (monthPart.length !== 2) return VALIDATION_MESSAGES.expiryMonth;
  if (yearPart.length !== 2) return VALIDATION_MESSAGES.expiryYear;

  return (
    validateExpiryMonth(monthPart) ||
    validateExpiryYear(yearPart.padStart(4, "20"))
  );
}

function validateExpiryMonth(month) {
  return !Validators.isValidMonth(month)
    ? VALIDATION_MESSAGES.expiryMonth
    : null;
}

function validateExpiryYear(year) {
  return !Validators.isValidExpirationYear(year)
    ? VALIDATION_MESSAGES.expiryYear
    : null;
}

// form validation
export function validateFirstName(name) {
  return !Validators.isNotEmpty(name) ? VALIDATION_MESSAGES.firstName : null;
}

export function validateLastName(name) {
  return !Validators.isNotEmpty(name) ? VALIDATION_MESSAGES.lastName : null;
}

export function validateCreditCard(cardNumber) {
  return !Validators.isValidCardNumber(cardNumber)
    ? VALIDATION_MESSAGES.cardNumber
    : null;
}

export function validateCvv(cvv, cardNumber) {
  const data = {
    cardNumber,
  };
  return !Validators.isValidCvv(cvv, data) ? VALIDATION_MESSAGES.cvv : null;
}

export function validateEmail(email) {
  return !Validators.isValidEmail(email) ? VALIDATION_MESSAGES.email : null;
}

export function validatePhone(phone) {
  return Validators.isNotEmpty(phone) &&
    (!Validators.isValidPhoneNumber(phone) || !Validators.isNANP(phone))
    ? VALIDATION_MESSAGES.phone
    : null;
}

export function validateAddress1(address1) {
  return !Validators.isNotEmpty(address1) ? VALIDATION_MESSAGES.address1 : null;
}

export function validateCity(city) {
  return !Validators.isNotEmpty(city) ? VALIDATION_MESSAGES.city : null;
}

export function validateState(state) {
  return !Validators.isNotEmpty(state) ? VALIDATION_MESSAGES.state : null;
}

export function validateZip(zip) {
  return !Validators.isNotEmpty(zip) ? VALIDATION_MESSAGES.zip : null;
}

function getCardNumberLimit(number) {
  return startsWith("3", number) ? "15" : "16";
}

export function onCardNumberChange(event, setFieldValue) {
  const cardNumber = event.target.value.replace(/[^0-9.]+/g, "");
  if (cardNumber.length <= getCardNumberLimit(cardNumber)) {
    setFieldValue("cardNumber", cardNumber);
  }
}

export function onExpiryBlur(event, setFieldValue, setFieldTouched) {
  const { value } = event.target;
  const strippedValue = value.replace(/[^0-9]+/g, "");

  const monthPart = strippedValue.slice(0, 2);
  const yearPart = strippedValue.slice(2, 4);

  setFieldValue("expiryYear", yearPart.padStart(4, "20"));
  setFieldValue("expiryMonth", monthPart);

  setFieldTouched("expiration");
  setFieldTouched("expiryMonth");
  setFieldTouched("expiryYear");
}

export function onExpirationUpdate(event, setFieldValue) {
  const { value } = event.target;
  const strippedValue = value.replace(/[^0-9]+/g, "");

  const monthPart = strippedValue.slice(0, 2);
  const yearPart = strippedValue.slice(2, 4);

  if (strippedValue.length >= 3) {
    setFieldValue("expiration", `${monthPart}/${yearPart}`);
  } else {
    setFieldValue("expiration", monthPart);
  }
}

export const initialCardInfo: CreditCardFormValues = {
  firstName: "",
  lastName: "",
  cardNumber: "",
  expiryMonth: "",
  expiryYear: "",
  expiration: "",
  cvv: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
};
